import { WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR } from "@/constants";
import { REVIEWS_WIDGET_NAMES } from "@/constants/widget-names";
import { globalProductIdManagerToken } from "@/tokens";
import { ProductReviewsMiniSliderWidgetDefinition } from "@apps/reviews/definitions";
import { ProductIdFromStaticProvider } from "@lib/ProductIdManager/ProductIdFromStaticProvider";
import { ProductIdManager } from "@lib/ProductIdManager/ProductIdManager";
import AnalyticsEventsCollectorInterface from "@modules/analytics_events_collector/AnalyticsEventsCollector/AnalyticsEventsCollectorInterface";
import { AnalyticsEventsFactory } from "@modules/analytics_events_collector/AnalyticsEventsCollector/AnalyticsEventsFactory";
import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
import WidgetInjectedSdkEvent from "@modules/sdk/common_sdk/events/WidgetInjectedSdkEvent";
import { dispatchSdkEvent } from "@modules/sdk/helpers";
import IntersectionObserverInjector from "@ui-utils/Injector/IntersectionObserverInjector";
import MutationObserverInjector from "@ui-utils/Injector/MutationObserverInjector";
import {
    parseBooleanDataAttr,
    parseIntDataAttr,
    parseIntFromGidDataAttr,
    parseOptionalBooleanDataAttr,
} from "@ui-utils/Injector/utils";
import { container } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

const OLD_REVIEW_PRODUCT_ID = "product_id";
const REVIEW_PRODUCT_ID = "gw-product-id";
const LOWEST_RATING_DATA_ATTR = "gw-lowest-rating";
const ONLY_PINNED_REVIEWS_DATA_ATTR = "gw-only-pinned-reviews";

const LOWEST_RATING_DEFAULT = 1;

class ProductReviewsMiniSliderWidgetInjector {
    private ioi: IntersectionObserverInjector;

    constructor(
        private readonly placeholderSelector: string,
        private readonly logger: LoggerInterface,
        private readonly eventsCollector: AnalyticsEventsCollectorInterface,
        private readonly analyticsEventsFactory: AnalyticsEventsFactory,
        private readonly dynamicImportManager: DynamicImportManager,
        private readonly moi: MutationObserverInjector
    ) {
        this.ioi = new IntersectionObserverInjector(
            IntersectionObserver,
            this.moi,
            this.placeholderSelector,
            {
                rootMargin: "0px 0px 0px 0px",
            }
        );
    }

    start(): void {
        this.logger.debug("ProductReviewsMiniSliderWidgetInjector start", {
            placeholderSelector: this.placeholderSelector,
        });

        this.ioi.targets$.subscribe((placeholder) => {
            this.logger?.debug(
                "ProductReviewsMiniSliderWidgetInjector found placeholder",
                {
                    placeholder,
                }
            );

            const widgetName =
                REVIEWS_WIDGET_NAMES.PRODUCT_REVIEWS_MINI_SLIDER_WIDGET;

            let productIdFromDataAttr =
                parseIntDataAttr(placeholder, REVIEW_PRODUCT_ID) ||
                parseIntFromGidDataAttr(placeholder, REVIEW_PRODUCT_ID);

            if (productIdFromDataAttr === null) {
                productIdFromDataAttr = parseIntDataAttr(
                    placeholder,
                    OLD_REVIEW_PRODUCT_ID
                );

                if (productIdFromDataAttr !== null) {
                    this.eventsCollector.pushEvent(
                        this.analyticsEventsFactory.createWidgetInjectedWithAttribute(
                            widgetName,
                            OLD_REVIEW_PRODUCT_ID
                        )
                    );
                }
            }

            if (productIdFromDataAttr === null) {
                this.logger.error(
                    `ProductReviewsMiniSliderWidget placeholder does not have data-${REVIEW_PRODUCT_ID} attribute.`
                );
            }

            const productIdManager =
                productIdFromDataAttr === null
                    ? container.resolve(globalProductIdManagerToken)
                    : new ProductIdManager();

            if (productIdFromDataAttr !== null) {
                productIdManager.registerProvider(
                    new ProductIdFromStaticProvider(productIdFromDataAttr)
                );
            }

            const lowestRatingFromDataAttr =
                parseIntDataAttr(placeholder, LOWEST_RATING_DATA_ATTR) ||
                LOWEST_RATING_DEFAULT;

            const onlyPinnedFromDataAttr = parseBooleanDataAttr(
                placeholder,
                ONLY_PINNED_REVIEWS_DATA_ATTR
            );

            void (async () => {
                const ProductReviewsMiniSliderWidget =
                    await this.dynamicImportManager.importDefault(
                        ProductReviewsMiniSliderWidgetDefinition,
                        true
                    );

                const widget = new ProductReviewsMiniSliderWidget(
                    {
                        target: placeholder,
                        lowestRating: lowestRatingFromDataAttr,
                        onlyPinnedReviews: onlyPinnedFromDataAttr,
                        isWidgetShadowRootEnabled: parseOptionalBooleanDataAttr(
                            placeholder,
                            WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR
                        ),
                    },
                    this.logger,
                    productIdManager
                );

                await widget.init();

                this.eventsCollector.pushEvent(
                    this.analyticsEventsFactory.createWidgetInjected(widgetName)
                );

                dispatchSdkEvent(new WidgetInjectedSdkEvent(widgetName));
            })();
        });

        this.ioi.start();
    }
}

export default ProductReviewsMiniSliderWidgetInjector;
